import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { cn } from "@/lib/utils";
import { useQuiz, useUpdateQuiz } from "@/hooks/useQuizzes";
import { QuizStatusBadge } from "@/components/quizzes/QuizStatusBadge";
import { ScoringEditor } from "@/components/scoring/ScoringEditor";
import { QuestionList } from "@/components/quizzes/QuestionList";
import { AddQuestionDialog } from "@/components/quizzes/AddQuestionDialog";
import { QuizImportDialog } from "@/components/quizzes/QuizImportDialog";
import { Button } from "@/components/ui/button";
import type { QuizQuestion } from "@/types/quiz";

type TabId = "fragen" | "scoring";

const TABS: { id: TabId; label: string }[] = [
  { id: "fragen", label: "Fragen" },
  { id: "scoring", label: "Scoring" },
];

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <div className="h-8 w-48 animate-pulse rounded bg-muted" />
      <div className="h-4 w-32 animate-pulse rounded bg-muted" />
      <div className="mt-6 space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-16 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
    </div>
  );
}

function renumberDisplayOrder(questions: QuizQuestion[]): QuizQuestion[] {
  return questions.map((q, i) => ({ ...q, displayOrder: i + 1 }));
}

export function QuizDetailPage() {
  const { quizId } = useParams<{ quizId: string }>();
  const { data: quiz, isLoading, isError, error } = useQuiz(quizId);
  const updateMutation = useUpdateQuiz();
  const [activeTab, setActiveTab] = useState<TabId>("fragen");

  const [localQuestions, setLocalQuestions] = useState<QuizQuestion[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importSuccessCount, setImportSuccessCount] = useState(0);
  const [editingQuestion, setEditingQuestion] = useState<
    QuizQuestion | undefined
  >(undefined);

  useEffect(() => {
    setLocalQuestions(quiz?.configuration?.questions ?? []);
    setIsDirty(false);
  }, [quiz]);

  const handleAddQuestion = useCallback(() => {
    setEditingQuestion(undefined);
    setDialogOpen(true);
  }, []);

  const handleEditQuestion = useCallback((question: QuizQuestion) => {
    setEditingQuestion(question);
    setDialogOpen(true);
  }, []);

  const handleDeleteQuestion = useCallback((question: QuizQuestion) => {
    setLocalQuestions((prev) => {
      const filtered = prev.filter(
        (q) => q.questionKey !== question.questionKey,
      );
      return renumberDisplayOrder(filtered);
    });
    setIsDirty(true);
  }, []);

  // Reorder only swaps array position + displayOrder. questionKey, options and
  // every option.id stay untouched, so the scoring_rules coupling holds.
  const handleMoveQuestionUp = useCallback((question: QuizQuestion) => {
    setLocalQuestions((prev) => {
      const index = prev.findIndex(
        (q) => q.questionKey === question.questionKey,
      );
      if (index <= 0) return prev;
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return renumberDisplayOrder(next);
    });
    setIsDirty(true);
  }, []);

  const handleMoveQuestionDown = useCallback((question: QuizQuestion) => {
    setLocalQuestions((prev) => {
      const index = prev.findIndex(
        (q) => q.questionKey === question.questionKey,
      );
      if (index < 0 || index >= prev.length - 1) return prev;
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return renumberDisplayOrder(next);
    });
    setIsDirty(true);
  }, []);

  const handleImportQuestions = useCallback(
    async (imported: QuizQuestion[]) => {
      if (!quiz || !quizId) return;

      const importMap = new Map(imported.map((q) => [q.questionKey, q]));
      const existingKeySet = new Set(localQuestions.map((q) => q.questionKey));
      const merged = localQuestions.map(
        (q) => importMap.get(q.questionKey) ?? q,
      );
      const newQuestions = imported.filter(
        (q) => !existingKeySet.has(q.questionKey),
      );
      const nextQuestions = renumberDisplayOrder([...merged, ...newQuestions]);

      setLocalQuestions(nextQuestions);
      setIsDirty(true);
      setSaveError("");
      setSaveSuccess(false);

      try {
        await updateMutation.mutateAsync({
          quizId,
          data: {
            configuration: {
              ...quiz.configuration,
              questions: nextQuestions,
            },
          },
        });
        setIsDirty(false);
        setImportDialogOpen(false);
        setImportSuccessCount(imported.length);
        setTimeout(() => setImportSuccessCount(0), 5000);
      } catch (err) {
        setImportDialogOpen(false);
        setSaveError(
          err instanceof Error
            ? `Fragen importiert, aber Speichern fehlgeschlagen. Bitte oben rechts auf 'Speichern' klicken. (${err.message})`
            : "Fragen importiert, aber Speichern fehlgeschlagen. Bitte oben rechts auf 'Speichern' klicken.",
        );
      }
    },
    [quiz, quizId, localQuestions, updateMutation],
  );

  const handleDialogSubmit = useCallback(
    (question: QuizQuestion) => {
      if (editingQuestion) {
        setLocalQuestions((prev) =>
          prev.map((q) =>
            q.questionKey === editingQuestion.questionKey ? question : q,
          ),
        );
      } else {
        setLocalQuestions((prev) => renumberDisplayOrder([...prev, question]));
      }
      setIsDirty(true);
      setDialogOpen(false);
      setEditingQuestion(undefined);
    },
    [editingQuestion],
  );

  const handleSave = useCallback(async () => {
    if (!quiz || !quizId) return;
    setSaveError("");
    setSaveSuccess(false);

    try {
      await updateMutation.mutateAsync({
        quizId,
        data: {
          configuration: {
            ...quiz.configuration,
            questions: localQuestions,
          },
        },
      });
      setIsDirty(false);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : "Ein Fehler ist aufgetreten.",
      );
    }
  }, [quiz, quizId, localQuestions, updateMutation]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Link
          to="/quizzes"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Zurück zu Quizzes
        </Link>
        <LoadingSkeleton />
      </div>
    );
  }

  if (isError || !quiz) {
    return (
      <div className="space-y-6">
        <Link
          to="/quizzes"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Zurück zu Quizzes
        </Link>
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
          <p className="text-sm text-red-700">
            Quiz konnte nicht geladen werden:{" "}
            {error instanceof Error ? error.message : "Unbekannter Fehler"}
          </p>
        </div>
      </div>
    );
  }

  const existingKeys = localQuestions.map((q) => q.questionKey);

  return (
    <div className="space-y-6">
      <Link
        to="/quizzes"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Zurück zu Quizzes
      </Link>

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">{quiz.title}</h1>
          <QuizStatusBadge status={quiz.status} />
        </div>
        {activeTab === "fragen" && (
          <div className="flex items-center gap-3">
            {importSuccessCount > 0 && (
              <span className="text-sm text-green-600">
                {importSuccessCount} Fragen importiert und gespeichert.
              </span>
            )}
            {saveSuccess && importSuccessCount === 0 && (
              <span className="text-sm text-green-600">
                Änderungen gespeichert
              </span>
            )}
            {saveError && (
              <span className="text-sm text-destructive">{saveError}</span>
            )}
            <Button
              onClick={handleSave}
              disabled={!isDirty || updateMutation.isPending}
              className="gap-1"
            >
              <Save className="h-4 w-4" />
              {updateMutation.isPending ? "Wird gespeichert..." : "Speichern"}
            </Button>
          </div>
        )}
      </div>

      {quiz.description && (
        <p className="text-muted-foreground">{quiz.description}</p>
      )}

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex gap-4" aria-label="Tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={cn(
                "border-b-2 px-1 py-2 text-sm font-medium transition-colors",
                activeTab === tab.id
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:border-muted-foreground/30 hover:text-foreground",
              )}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === "fragen" && (
        <>
          <QuestionList
            questions={localQuestions}
            onAdd={handleAddQuestion}
            onImport={() => setImportDialogOpen(true)}
            onEdit={handleEditQuestion}
            onDelete={handleDeleteQuestion}
            onMoveUp={handleMoveQuestionUp}
            onMoveDown={handleMoveQuestionDown}
          />
          <AddQuestionDialog
            open={dialogOpen}
            onClose={() => {
              setDialogOpen(false);
              setEditingQuestion(undefined);
            }}
            onSubmit={handleDialogSubmit}
            existingKeys={
              editingQuestion
                ? existingKeys.filter((k) => k !== editingQuestion.questionKey)
                : existingKeys
            }
            editQuestion={editingQuestion}
            nextDisplayOrder={localQuestions.length + 1}
          />
          <QuizImportDialog
            open={importDialogOpen}
            onClose={() => setImportDialogOpen(false)}
            onImport={handleImportQuestions}
            existingKeys={existingKeys}
            isSaving={updateMutation.isPending}
          />
        </>
      )}

      {activeTab === "scoring" && (
        <ScoringEditor quizId={quiz.quizId} questions={localQuestions} />
      )}
    </div>
  );
}
